<template>
    <div>
        <b-row v-if="prescriber.practiceName">
            <b-col>
                <label><strong>Practice Name: </strong></label>
                {{ prescriber.practiceName }}
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <label><strong>Name: </strong></label>
                {{ prescriber.displayNamesForPerson() }}
            </b-col>
        </b-row>
        <hr />
        <b-row>
            <b-col>
                <label><strong>NPI: </strong></label>
                {{ prescriber.npiNumber }}
                <a v-if="prescriber.npiNumber?.length" href="#" class="mx-1">
                    <i class="fas fa-copy" @click.prevent="copyText(prescriber.npiNumber)" />
                </a>
            </b-col>
            <b-col v-if="prescriber.deaNumber">
                <label><strong>DEA: </strong></label>
                {{ prescriber.deaNumber }}
                <a href="#" class="mx-1">
                    <i class="fas fa-copy" @click.prevent="copyText(prescriber.deaNumber)" />
                </a>
            </b-col>
        </b-row>
        <b-row v-if="prescriber.stateLicenseNumber">
            <b-col>
                <label><strong>State License: </strong></label>
                {{ prescriber.stateLicenseNumber }}
            </b-col>
        </b-row>
        <b-row v-if="prescriber.specialities?.length">
            <b-col>
                <label><strong>Specialties: </strong></label>
                {{ specialtiesDisplay }}
            </b-col>
        </b-row>
        <hr v-if="prescriber.phoneNumber || prescriber.altNumber || prescriber.faxNumber || prescriber.altFaxNumber || prescriber.emailAddress" />
        <b-row v-if="prescriber.phoneNumber">
            <b-col>
                <label><strong>Phone: </strong></label>
                {{ prescriber.phoneNumber }}
                <a href="#" class="mx-1">
                    <i class="fas fa-copy" @click.prevent="copyText(prescriber.phoneNumber)" />
                </a>
            </b-col>
            <b-col v-if="prescriber.altNumber">
                <label><strong>Alt Phone: </strong></label>
                {{ prescriber.altNumber }}
                <a href="#" class="mx-1">
                    <i class="fas fa-copy" @click.prevent="copyText(prescriber.altNumber)" />
                </a>
            </b-col>
        </b-row>
        <b-row v-if="prescriber.faxNumber">
            <b-col>
                <label><strong>Fax: </strong></label>
                {{ prescriber.faxNumber }}
                <a href="#" class="mx-1">
                    <i class="fas fa-copy" @click.prevent="copyText(prescriber.faxNumber)" />
                </a>
            </b-col>
            <b-col v-if="prescriber.altFaxNumber">
                <label><strong>Alt Fax: </strong></label>
                {{ prescriber.altFaxNumber }}
                <a href="#" class="mx-1">
                    <i class="fas fa-copy" @click.prevent="copyText(prescriber.altFaxNumber)" />
                </a>
            </b-col>
        </b-row>
        <b-row v-if="prescriber.emailAddress?.length">
            <b-col>
                <label><strong>Email: </strong></label>
                {{ prescriber.emailAddress }}
                <a href="#" class="mx-1">
                    <i class="fas fa-copy" @click.prevent="copyText(prescriber.emailAddress)" />
                </a>
            </b-col>
        </b-row>
        <hr />
        <b-row>
            <b-col>
                <label><strong>Address: </strong></label>
                <br />
                {{ prescriber.address1 }}<br />
                <template v-if="prescriber.address2">
                    {{ prescriber.address2 }}<br />
                </template>
                {{ prescriber.addressCity }}, {{ prescriber.addressState }}
                {{ prescriber.addressZip }}
            </b-col>
        </b-row>
        <hr v-if="prescriber.hoursOfOperation || prescriber.medicaidNumber || communicationPreferenceName" />
        <b-row v-if="prescriber.hoursOfOperation">
            <b-col>
                <label><strong>Hours of Operation: </strong></label>
                {{ prescriber.hoursOfOperation }}
            </b-col>
        </b-row>
        <b-row v-if="prescriber.medicaidNumber">
            <b-col>
                <label><strong>Medicaid Number: </strong></label>
                {{ prescriber.medicaidNumber }}
            </b-col>
        </b-row>
        <b-row v-if="communicationPreferenceName">
            <b-col>
                <label><strong>Communication Preference: </strong></label>
                {{ communicationPreferenceName }}
            </b-col>
        </b-row>
        <b-row v-if="prescriber.sureScriptsId">
            <b-col>
                <label><strong>SureScripts ID: </strong></label>
                {{ prescriber.sureScriptsId }}
            </b-col>
        </b-row>
        <hr v-if="prescriber.contacts?.length" />
        <b-row v-if="prescriber.contacts?.length">
            <b-col>
                <label><strong>Contacts:</strong></label>
                <div v-for="contact in prescriber.contacts" :key="contact.id" class="ml-3 mb-2">
                    <div>
                        <strong>{{ contact.firstName }} {{ contact.lastName }}</strong>
                        <span v-if="contact.title"> - {{ contact.title }}</span>
                    </div>
                    <div v-if="contact.phoneNumber">
                        Phone: {{ contact.phoneNumber }}
                        <a href="#" class="mx-1">
                            <i class="fas fa-copy" @click.prevent="copyText(contact.phoneNumber)" />
                        </a>
                    </div>
                    <div v-if="contact.note" class="text-muted small">
                        {{ contact.note }}
                    </div>
                </div>
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import { Prescriber, PrescriberCommunicationPreference } from "@/models/Prescriber";

@Component({
    name: "PrescriberDemographicsComponent"
})
export default class PrescriberDemographicsComponent extends Vue {
    @Prop() private value!: Prescriber;

    get prescriber(): Prescriber {
        if (!(this.value instanceof Prescriber))
            return Object.assign(new Prescriber(), this.value);
        return this.value;
    }

    get specialtiesDisplay(): string {
        if (!this.prescriber.specialities?.length) return "";
        return this.prescriber.specialities.map(s => s.name).join(", ");
    }

    get communicationPreferenceName(): string {
        if (this.prescriber.communicationPreference === undefined) return "";

        switch (this.prescriber.communicationPreference) {
            case PrescriberCommunicationPreference.email: return "Email";
            case PrescriberCommunicationPreference.fax: return "Fax";
            case PrescriberCommunicationPreference.call: return "Call";
            case PrescriberCommunicationPreference.portal: return "Portal";
            default: return "";
        }
    }

    copyText(text: string) {
        navigator.clipboard.writeText(text);
    }
}
</script>
